/* eslint-disable react-hooks/exhaustive-deps */
import React, { RefObject, useEffect, useRef, useState } from "react";
import { FileUploaderComponent, useFileManagement } from "../../../services/file-management";
import { useAuthContext } from "../../../contexts/auth-context";
import { apiRequestGet, apiRequestSecuredDelete, apiRequestSecuredPatch } from "../../../lib/common/api";
import { Spinner } from "../../common/spinner";
import { DEVICES_SIZES_IN_PX, GALLERY_LIMIT } from "../../../lib/common/constants";
import { TProcessStates } from "../../../lib/types/general";
import { evaluators, useWindowSizeMatch } from "../../../lib/window-size-matcher";
import { ErrorMessageSticky } from "../../common/alert-message";
type TLabImageGallery = {
    user_id: number;
    image_id: string;
    user_guid: string;
    image_label: string;
    description: string;
};
type TImgInfo = { imgId: string; imgDescription?: string };

const Gallery_URLS = {
    getGallery: "files/gallery/",
    deleteImgGallery: "files/delete",
    addImgDescription: (image_id: string, image_description: string) =>
        `files/gallery/image_id/${encodeURIComponent(image_id)}/description/${encodeURIComponent(image_description)}`,
};

const useGalleryImages = (userId: number, refCarousel: RefObject<HTMLDivElement>) => {
    const [images, setImages] = useState<{ [userGUID: string]: TImgInfo[] }>({});
    const [canAddImg, setCanAddImg] = useState<boolean>(true);
    const { getUserTokenId } = useAuthContext();
    const { configureCarousel, setImgIndex } = useCarouselMobile();

    const getGallery = async () => {
        try {
            const response = await apiRequestGet<{
                data: {
                    rows: TLabImageGallery[];
                    countImages: number | undefined;
                };
            }>(`${Gallery_URLS.getGallery}${userId}`);

            const imagesData = response.data?.data.rows || [];
            const imagesCount = response.data?.data.countImages ?? 0;

            return { images: imagesData, imageCount: imagesCount };
        } catch (error) {
            console.error("Error fetching gallery images:", error);
            return { images: [], imageCount: 0 };
        }
    };

    const deleteImageGallery = async (image_id: string): Promise<any> => {
        return apiRequestSecuredDelete(Gallery_URLS.deleteImgGallery, await getUserTokenId(), {
            image_id: image_id,
        });
    };
    const refreshGallery = async () => {
        const updatedGallery = await getGallery();
        setImgIndex(0);
        configureCarousel(refCarousel);
        const updatedImagesObject: { [userId: string]: TImgInfo[] } = {};

        if (Array.isArray(updatedGallery.images)) {
            updatedGallery.images.forEach((item) => {
                if (!updatedImagesObject[item.user_guid]) {
                    updatedImagesObject[item.user_guid] = [];
                }
                updatedImagesObject[item.user_guid].push({
                    imgId: item.image_id,
                    imgDescription: item.description,
                });
            });
        }
        setImages(updatedImagesObject);
        setCanAddImg(updatedGallery.imageCount < GALLERY_LIMIT);
    };

    const addImgDescription = async (image_id: string, image_description?: string) => {
        const description = image_description?.trim() ?? "";
        return apiRequestSecuredPatch(Gallery_URLS.addImgDescription(image_id, description), await getUserTokenId());
    };

    useEffect(() => {
        getGallery().then((data) => {
            const imagesObject: { [userId: string]: TImgInfo[] } = {};

            if (Array.isArray(data.images)) {
                data.images.forEach((item) => {
                    if (!imagesObject[item.user_guid]) {
                        imagesObject[item.user_guid] = [];
                    }
                    imagesObject[item.user_guid].push({
                        imgId: item.image_id,
                        imgDescription: item.description,
                    });
                });
            }
            setImages(imagesObject);
            setCanAddImg(data?.imageCount < GALLERY_LIMIT);
        });
    }, [userId]);

    return {
        images,
        deleteImageGallery,
        refreshGallery,
        canAddImg,
        addImgDescription,
    };
};

type TGallerySectionProps = {
    userId: number;
    editable: boolean;
    isSomePartBeingEdited: boolean;
    setIsSomePartBeingEdited: (value: boolean) => void;
    setUploadErrorMessage?: (value: string) => void;
    title: string;
    placeholder: string;
    titleIcon?: string;
    onboardingStepCompleted?: () => void;
    isIncognito: boolean;
};

export const useCarouselMobile = () => {
    const [imgIndex, setImgIndex] = useState(0);

    let [carouselBootstrapObject, setCarouselBootstrapObject] = useState<{
        instance: { to: (index: number) => void } | undefined;
    }>({
        instance: undefined,
    });
    const configureCarousel = (refCarousel: RefObject<HTMLDivElement>) => {
        if (!refCarousel.current) {
            setTimeout(() => {
                configureCarousel(refCarousel);
            }, 200);
            return;
        }
        setCarouselBootstrapObject({ instance: new window.bootstrap.Carousel(refCarousel.current) });

        refCarousel.current.addEventListener("slide.bs.carousel", (event: Event) => {
            const e = event as unknown as { relatedTarget: Element };

            const items = Array.from((event.target as any).querySelectorAll(".carousel-item"));
            const newIndex = items.indexOf(e.relatedTarget);
            if (newIndex >= 0) {
                setImgIndex(newIndex);
            }
        });
    };

    const onClickHandler = (e: React.MouseEvent, index: number) => {
        e.preventDefault();
        if (carouselBootstrapObject.instance) carouselBootstrapObject.instance.to(index);
    };

    return { imgIndex, setImgIndex, configureCarousel, onClickHandler };
};

export const GallerySection = ({
    userId,
    setIsSomePartBeingEdited,
    editable,
    title,
    placeholder,
    titleIcon,
    onboardingStepCompleted,
    isIncognito,
}: TGallerySectionProps) => {
    const [isEditingGallery, setIsEditingGallery] = useState(false);
    const refGallery1 = useRef<HTMLDivElement>(null);
    const refCarousel = useRef<HTMLDivElement>(null);
    const { images, refreshGallery, deleteImageGallery, canAddImg, addImgDescription } = useGalleryImages(userId, refCarousel);
    const { getImagePath, makeDroppableCapable, state } = useFileManagement();
    const [editingImageId, setEditingImageId] = useState<string | undefined>(undefined);
    const [newDescription, setNewDescription] = useState<{ [key: string]: string }>({});
    const [workingState, setWorkingState] = useState<TProcessStates>("idle");
    const [editingDescription, setEditingDescription] = useState(false);
    const [galleryUploadError, setGalleryUploadError] = useState("");
    const [galleryInappropriateContent, setGalleryInappropriateContent] = useState(false);
    const [galleryUnsupportedImageType, setGalleryUnsupportedImageType] = useState(false);
    const { imgIndex, setImgIndex, configureCarousel, onClickHandler } = useCarouselMobile();

    const imagesMap = Object.entries(images).flatMap(([userId, imageIds]) => imageIds.map((imageInfo) => ({ ...imageInfo, userId })));

    const editToggleHandler = () => {
        setIsEditingGallery((prevState) => {
            setIsSomePartBeingEdited(!prevState);
            if (prevState) configureCarousel(refCarousel);
            return !prevState;
        });
        if (isEditingGallery) {
            setGalleryUploadError("");
            setGalleryInappropriateContent(false);
            setGalleryUnsupportedImageType(false);
        }
    };

    const editOneImgDescription = async (imgId: string) => {
        setEditingDescription(true);
        setEditingImageId(imgId);
    };

    const saveImgDescription = async (imgId: string, newDescription?: string) => {
        setWorkingState("working");

        await addImgDescription(imgId, newDescription);
        await refreshGallery();

        setWorkingState("done");
        setEditingDescription(false);
        setEditingImageId(undefined);
        setNewDescription((prev) => ({
            ...prev,
            [imgId]: newDescription || "",
        }));
    };

    const deleteImage = async (image_id: string) => {
        await deleteImageGallery(image_id);
        await refreshGallery();
        await onboardingStepCompleted?.();
    };

    const updateGallery1 = async (newFile?: string, inappropriateContent?: boolean, unsupportedImageType?: boolean, errorMessage?: string) => {
        if (newFile) await refreshGallery();
        if (errorMessage) {
            setGalleryUploadError(errorMessage);
            setGalleryInappropriateContent(false);
            setGalleryUnsupportedImageType(false);
        } else if (inappropriateContent) {
            setGalleryUploadError("");
            setGalleryInappropriateContent(true);
            setGalleryUnsupportedImageType(false);
        } else if (unsupportedImageType) {
            setGalleryUploadError("");
            setGalleryInappropriateContent(false);
            setGalleryUnsupportedImageType(true);
        } else {
            if (!newFile) {
            } else {
                setGalleryUploadError("");
                setGalleryInappropriateContent(false);
                setGalleryUnsupportedImageType(false);
            }
        }
    };

    const { conditionFulfilled: isSmallDevice } = useWindowSizeMatch({
        condition: evaluators.lessThan(DEVICES_SIZES_IN_PX.small),
    });

    useEffect(() => {
        makeDroppableCapable(
            refGallery1,
            "gallery1",
            "image",
            "gallery",
            async (newfile, inappropriateContent, unsupportedImageType, errorMessage) => {
                await updateGallery1(newfile, inappropriateContent, unsupportedImageType, errorMessage);
                await onboardingStepCompleted?.();
            },
            GALLERY_LIMIT - imagesMap.length,
        );
        setImgIndex(0);
    }, [isEditingGallery, images]);

    useEffect(() => {
        configureCarousel(refCarousel);
    }, []);
    return (
        <>
            <div className={`title-section-alignment ${titleIcon ? "pb-4" : "p-2 p-md-4"}`}>
                <h5 className="fw-bold">
                    {titleIcon ? <i className={`fa-solid ${titleIcon} me-3 text-info`}></i> : <></>}
                    {title}
                </h5>
                {editable && !isEditingGallery && (
                    <div className="fileTooltip rightEdit ms-auto ms-lg-2" data-tooltip=" Edit" role="button" onClick={editToggleHandler}>
                        <i data-testid="edit-icon" className="fa-solid fa-pen-to-square fa-xs p-2 text-center shadow editIcon"></i>
                    </div>
                )}
            </div>
            {/* when there is no image in gallery */}
            {!editable && (Object.keys(images).length === 0 || Object.values(images).every((imageIds) => imageIds.length === 0)) && (
                <div>
                    <div className="border rounded mx-md-3 p-4 d-flex justify-content-center flex-column align-items-center">
                        <i className="fa-solid fa-images mb-2 icon-no-photos"></i>
                    </div>
                </div>
            )}
            {editable && !isEditingGallery && (Object.keys(images).length === 0 || Object.values(images).every((imageIds) => imageIds.length === 0)) && (
                <div>
                    <div className="border rounded mx-md-3 p-4 d-flex justify-content-center flex-column align-items-center">
                        <i className="fa-solid fa-images mb-2 icon-no-photos"></i>
                        <p className="m-0 text-center" style={{ color: "#a5a5a5" }}>
                            <strong> Enhance your profile by creating a photo gallery! </strong>
                            <br />
                            {placeholder}
                        </p>
                    </div>
                </div>
            )}
            {/* when is editing */}
            {editable && isEditingGallery && (
                <>
                    <div className="mb-3">
                        <p className="px-md-4 pb-md-2">
                            <small>
                                Add up to 6 images to make your profile more engaging and stand out. <br /> Just make sure your images are either public domain
                                or that you have permission to use them!
                            </small>
                        </p>
                        {/* grid */}
                        <div className="row g-3 px-2">
                            {Object.entries(images).map(([userId, imageInfo]) =>
                                imageInfo.map((imgInfo) => (
                                    <div className="col-12 col-sm-6 col-xl-4 d-flex" key={`image_id_${imgInfo.imgId}`}>
                                        <div className="gallery-card flex-fill bg-white shadow-sm rounded-3">
                                            <div className="gallery-card-media">
                                                <img
                                                    src={getImagePath(imgInfo.imgId, "gallery", userId)}
                                                    alt={imgInfo.imgDescription || "Gallery image"}
                                                    draggable={false}
                                                    onContextMenu={(event) => event.preventDefault()}
                                                />
                                            </div>
                                            <div className="gallery-card-content small text-muted">
                                                {editingImageId === imgInfo.imgId ? (
                                                    <>
                                                        <textarea
                                                            className="form-control form-control-sm"
                                                            placeholder="Write a short description here"
                                                            maxLength={150}
                                                            value={newDescription[imgInfo.imgId] ?? imgInfo.imgDescription ?? ""}
                                                            onChange={(e) =>
                                                                setNewDescription({
                                                                    ...newDescription,
                                                                    [imgInfo.imgId]: e.target.value,
                                                                })
                                                            }
                                                            rows={2}
                                                        />
                                                        <div className="mt-2 d-flex justify-content-end gap-2">
                                                            <button
                                                                className="btn btn-primary btn-sm"
                                                                onClick={() => saveImgDescription(imgInfo.imgId, newDescription[imgInfo.imgId] ?? "")}
                                                            >
                                                                {workingState === "working" ? <Spinner /> : "Save"}
                                                            </button>
                                                            <button
                                                                className="btn btn-outline-secondary btn-sm"
                                                                onClick={() => {
                                                                    setEditingImageId(undefined);
                                                                    setEditingDescription(false);
                                                                }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <p className="m-0 text-muted small text-break">{imgInfo.imgDescription || <em>No description</em>}</p>
                                                )}
                                            </div>
                                            {!editingDescription && (
                                                <div className="gallery-actions">
                                                    <button className="btn btn-light btn-sm" onClick={() => editOneImgDescription(imgInfo.imgId)}>
                                                        <i className="fa-solid fa-pen"></i>
                                                    </button>
                                                    <button className="btn btn-light btn-sm text-danger" onClick={() => deleteImage(imgInfo.imgId)}>
                                                        <i className="fa-solid fa-trash"></i>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )),
                            )}
                            {canAddImg && (
                                <div className="col-12 col-sm-6 col-xl-4 d-flex">
                                    <div
                                        ref={refGallery1}
                                        className="gallery-card gallery-card_gallery-drag-drop rounded dashed-border border-primary d-flex flex-column justify-content-center align-items-center"
                                    >
                                        {state !== "working" && (
                                            <>
                                                <i className="fa-solid fa-cloud-upload-alt mb-2 icon-photos"></i>
                                                <p className="d-none d-md-block small">Drag and drop new images</p>
                                                <div className="">
                                                    <FileUploaderComponent multiple relatedComponent={refGallery1} tooltipPosition="right" fileToolTipText="" />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        {!canAddImg && <p className="fw-bold text-primary text-center mt-3">You can only add up to {GALLERY_LIMIT} images.</p>}
                        <div className="px-2 px-md-4 mt-3">
                            <button className="btn btn-primary shadow fw-bold py-2 w-100" disabled={state === "working"} onClick={editToggleHandler}>
                                Save and Finish Gallery
                            </button>
                        </div>
                        {(galleryUploadError || galleryInappropriateContent || galleryUnsupportedImageType) && (
                            <div className="px-2 px-md-4 mt-3">
                                <ErrorMessageSticky
                                    closeClickHandler={() => {
                                        setGalleryUploadError("");
                                        setGalleryInappropriateContent(false);
                                        setGalleryUnsupportedImageType(false);
                                    }}
                                    message={
                                        galleryUploadError
                                            ? galleryUploadError
                                            : galleryInappropriateContent
                                              ? "Image not accepted. It may contain inappropriate content. Please try again with a suitable image."
                                              : galleryUnsupportedImageType
                                                ? "Image format not accepted. Please use JPEG, JPG, PNG, HEIC, or HEIF formats."
                                                : ""
                                    }
                                />
                            </div>
                        )}
                    </div>
                </>
            )}
            {/* carrousel not editing*/}
            {!isEditingGallery && !isSmallDevice.fulfilled && (
                <div
                    className={
                        " col mt-2 gallery-container " + (editable ? "gallery-container-width" : "") + (isIncognito && !editable ? " lab-can-not-see" : "")
                    }
                >
                    <div className="gallery-scroll-track">
                        {Object.entries(images).map(([userId, imageIds]) =>
                            imageIds.map((imageInfo) => (
                                <div key={imageInfo.imgId} className="gallery-scroll-item">
                                    <div className="gallery-image">
                                        <img
                                            src={getImagePath(imageInfo.imgId, "gallery", userId)}
                                            alt={imageInfo.imgDescription || "Gallery image"}
                                            draggable={false}
                                            onContextMenu={(event) => event.preventDefault()}
                                        />
                                    </div>
                                    <div className="gallery-scroll-caption small text-muted text-break">
                                        {imageInfo.imgDescription ? <p>{imageInfo.imgDescription}</p> : <span className="visually-hidden">No description</span>}
                                    </div>
                                </div>
                            )),
                        )}
                    </div>
                </div>
            )}

            {!isEditingGallery && isSmallDevice.fulfilled && (
                <>
                    <div
                        className={"carousel carousel-dark slide p-0" + (isIncognito && !editable ? " lab-can-not-see" : "")}
                        data-bs-ride="false"
                        ref={refCarousel}
                    >
                        <div className="carousel-inner gallery-mobile-media">
                            {imagesMap.map((imageInfo, idx) => (
                                <div key={imageInfo.imgId} className={`carousel-item  ${idx === 0 ? "active " : ""}`}>
                                    <img
                                        src={getImagePath(imageInfo.imgId, "gallery", imageInfo.userId)}
                                        alt={imageInfo.imgDescription || "Gallery image"}
                                        className="d-block w-100"
                                        draggable={false}
                                        onContextMenu={(event) => event.preventDefault()}
                                    />
                                </div>
                            ))}
                        </div>
                        <div className="carousel-indicators position-static">
                            {imagesMap.map((_, idx) => (
                                <button
                                    key={`image-${idx}_indicator_slide_gallery`}
                                    type="button"
                                    data-bs-target
                                    data-bs-slide-to={idx}
                                    className={idx === imgIndex ? "active" : ""}
                                    aria-current={idx === imgIndex ? "true" : undefined}
                                    aria-label={`Slide ${idx + 1}`}
                                    onClick={(e) => {
                                        onClickHandler(e, idx);
                                    }}
                                ></button>
                            ))}
                        </div>
                        <div className="gallery-mobile-caption text-dark">
                            {imagesMap[imgIndex]?.imgDescription ? <p className=" mb-0">{imagesMap[imgIndex].imgDescription}</p> : null}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};
